import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Collapse,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Pets as PetsIcon,
  CalendarToday as CalendarIcon,
  Logout as LogoutIcon,
  MedicalServices as MedicalIcon,
  Medication as RxIcon,
  QueuePlayNext as QueueIcon,
  Receipt as InvoiceIcon,
  Inventory as InventoryIcon,
  MedicalInformation as ServicesIcon,
  ShoppingCart as PurchaseOrderIcon,
  BarChart as ReportsIcon,
  AdminPanelSettings as AdminIcon,
  Vaccines as VaccinesIcon,
  LocalPharmacy as ControlledDrugIcon,
  Biotech as BiotechIcon,
  Campaign as CampaignIcon,
  Assessment as CustomReportIcon,
  AccountBalance as PaymentPlanIcon,
  Hotel as BoardingIcon,
  Science as LabIcon,
  NotificationsActive as RemindersIcon,
  Notifications as NotificationsIcon,
  Psychology as AIIcon,
  AccountBalance as AccountingIcon,
  CalendarViewWeek as RosterIcon,
  BugReport as LogsIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/authStore';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

interface NavGroup {
  label: string;
  items: { text: string; icon: React.ReactNode; path: string }[];
}

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Clinical: true,
    Business: true,
  });
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await api.get<{ count: number }>(API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT);
        setUnreadCount(res.count ?? 0);
      } catch {
        // silently ignore
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleLogout = () => { logout(); navigate('/login'); };

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isSuperAdmin = user?.permissions?.includes('super_admin') || user?.role_name === 'Super Admin';
  const canAdmin = user?.permissions?.includes('manage_users') || isSuperAdmin;

  const coreItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
    { text: 'Patients', icon: <PetsIcon />, path: '/patients' },
    { text: 'Appointments', icon: <CalendarIcon />, path: '/appointments' },
  ];

  const groups: NavGroup[] = [
    {
      label: 'Clinical',
      items: [
        { text: 'Medical Records', icon: <MedicalIcon />, path: '/medical-records' },
        { text: 'Prescriptions', icon: <RxIcon />, path: '/prescriptions' },
        { text: 'Vaccinations', icon: <VaccinesIcon />, path: '/vaccinations' },
        { text: 'Lab Results', icon: <LabIcon />, path: '/lab-results' },
        { text: 'Controlled Drugs', icon: <ControlledDrugIcon />, path: '/controlled-drugs' },
        { text: 'Surgeries', icon: <BiotechIcon />, path: '/surgeries' },
        { text: 'Queue', icon: <QueueIcon />, path: '/queue' },
        { text: 'AI Assist', icon: <AIIcon />, path: '/ai-assist' },
      ],
    },
    {
      label: 'Business',
      items: [
        { text: 'Invoicing', icon: <InvoiceIcon />, path: '/invoicing' },
        { text: 'Payment Plans', icon: <PaymentPlanIcon />, path: '/payment-plans' },
        { text: 'Boarding', icon: <BoardingIcon />, path: '/boarding' },
        { text: 'Reminders', icon: <RemindersIcon />, path: '/reminders' },
        { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
        { text: 'Services', icon: <ServicesIcon />, path: '/services' },
        { text: 'Purchase Orders', icon: <PurchaseOrderIcon />, path: '/purchase-orders' },
        { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
        { text: 'Custom Reports', icon: <CustomReportIcon />, path: '/custom-reports' },
        { text: 'Recall Campaigns', icon: <CampaignIcon />, path: '/recall-campaigns' },
        { text: 'Accounting', icon: <AccountingIcon />, path: '/accounting' },
      ],
    },
    {
      label: 'Staff',
      items: [
        { text: 'Roster', icon: <RosterIcon />, path: '/roster' },
      ],
    },
    {
      label: 'System',
      items: [
        { text: 'Error Logs', icon: <LogsIcon />, path: '/logs' },
      ],
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Vet Clinic
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {coreItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={isActive(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      {groups.map((group) => (
        <Box key={group.label}>
          <ListItemButton onClick={() => toggleGroup(group.label)}>
            <ListItemText
              primary={group.label}
              primaryTypographyProps={{ variant: 'overline', color: 'textSecondary', fontWeight: 700 }}
            />
            {openGroups[group.label] ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={openGroups[group.label]} timeout="auto">
            <List disablePadding>
              {group.items.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    sx={{ pl: 3 }}
                    onClick={() => navigate(item.path)}
                    selected={isActive(item.path)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
          <Divider />
        </Box>
      ))}
      {canAdmin && (
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/admin')} selected={isActive('/admin')}>
              <ListItemIcon><AdminIcon /></ListItemIcon>
              <ListItemText primary="Admin" />
            </ListItemButton>
          </ListItem>
        </List>
      )}
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/notifications')} selected={isActive('/notifications')}>
            <ListItemIcon>
              <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Notifications" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.full_name || 'Veterinary Clinic'}
          </Typography>
          <Tooltip title="Notifications">
            <IconButton color="inherit" onClick={() => navigate('/notifications')} sx={{ mr: 1 }}>
              <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
